import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, CheckCircle2, Clock3, Phone, PhoneCall, Route, Search, UserRoundCheck } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import SEOHead from "@/components/SEOHead";
import CalendlyModal from "@/components/CalendlyModal";
import { signupUrl } from "@/lib/productApp";

export interface IntentSection { title:string; intro?:string; paragraphs?:string[]; bullets?:string[]; }
export interface IntentComparison { title:string; intro?:string; headers:[string,string,string]; rows:Array<[string,string,string]>; }
export interface IntentFaq { question:string; answer:string; }
export interface IntentRelatedLink { to:string; label:string; description:string; }
export type AcquisitionVisual = "standard" | "permanence" | "qualification" | "missed" | "accueil" | "agent";

export interface AcquisitionHero {
  visual: AcquisitionVisual;
  visualLabel: string;
  visualTitle: string;
  visualText: string;
  secondaryCtaLabel?: string;
  showPhoneDemo?: boolean;
  strip: [string, string, string];
}

interface IntentLandingLayoutProps {
  canonical:string; title:string; description:string; eyebrow:string; h1:string; lede:string;
  definition:ReactNode; serviceType:string; primaryCtaLabel:string; sections:IntentSection[];
  comparison?:IntentComparison; faq:IntentFaq[]; related:IntentRelatedLink[]; ctaTitle:string; ctaText:string;
  acquisitionHero?: AcquisitionHero;
}
const BASE_URL="https://immovox.fr";

function HeroVisual({ hero }: { hero: AcquisitionHero }) {
  if (hero.visual === "agent") {
    return (
      <div className="relative h-full min-h-[520px] overflow-hidden bg-[hsl(157_24%_15%)] p-7 text-background md:p-9">
        <p className="font-mono text-[10px] uppercase tracking-[.18em] text-background/55">{hero.visualLabel}</p>
        <div className="mt-9 border-y border-background/20">
          {[
            ["01", "Appel entrant", PhoneCall],
            ["02", "Compréhension du motif", Search],
            ["03", "Question métier utile", UserRoundCheck],
            ["04", "Rendez-vous, transfert ou rappel", Calendar],
          ].map(([n,label,Icon]) => {
            const I = Icon as typeof PhoneCall;
            return (
              <div key={label as string} className="grid grid-cols-[42px_36px_1fr] items-center gap-3 border-b border-background/15 py-5 last:border-b-0">
                <span className="font-mono text-[10px] text-background/45">{n as string}</span>
                <I className="h-4 w-4 text-background/70" />
                <span className="text-sm font-semibold">{label as string}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-8">
          <p className="text-2xl font-semibold tracking-[-.03em]">{hero.visualTitle}</p>
          <p className="mt-3 text-[15px] leading-7 text-background/68">{hero.visualText}</p>
        </div>
      </div>
    );
  }

  if (hero.visual === "accueil") {
    return (
      <div className="relative h-full min-h-[520px] overflow-hidden">
        <img src="/hero.png" alt="" className="absolute inset-0 h-full w-full object-cover object-[62%_center]" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" />
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-background via-background/75 to-transparent" />
        <div className="absolute bottom-7 left-7 right-7 bg-background/[.985] p-5 shadow-warm-lg">
          <p className="font-mono text-[10px] uppercase tracking-[.15em] text-primary">{hero.visualLabel}</p>
          <p className="mt-3 text-[20px] font-semibold tracking-[-.025em]">{hero.visualTitle}</p>
          <p className="mt-2 text-[14px] leading-6 text-muted-foreground">{hero.visualText}</p>
          <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-2 border-t border-border pt-4 text-xs">
            <span>Vendeur → estimation</span>
            <span>Acquéreur → visite</span>
            <span>Location → dossier</span>
            <span>Gestion → interlocuteur</span>
          </div>
        </div>
      </div>
    );
  }

  if (hero.visual === "permanence") {
    return (
      <div className="relative h-full min-h-[520px] overflow-hidden bg-[hsl(157_24%_15%)] p-7 text-background md:p-9">
        <div className="absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full border border-background/10" />
        <p className="font-mono text-[10px] uppercase tracking-[.18em] text-background/55">{hero.visualLabel}</p>
        <div className="mt-12 max-w-[420px]">
          <p className="text-3xl font-semibold tracking-[-.035em]">{hero.visualTitle}</p>
          <p className="mt-4 text-[15px] leading-7 text-background/65">{hero.visualText}</p>
        </div>
        <div className="absolute bottom-8 left-7 right-7 border-t border-background/20 md:left-9 md:right-9">
          {[
            ["18:42", "Agence fermée"],
            ["18:43", "Appel vendeur entrant"],
            ["18:43", "Immovox répond et qualifie"],
            ["Demain", "Rappel préparé pour l'équipe"],
          ].map(([time,label],i)=>(
            <div key={label} className="grid grid-cols-[62px_1fr] gap-4 border-b border-background/15 py-4">
              <span className="font-mono text-[10px] text-background/45">{time}</span>
              <span className={i===2 ? "text-sm font-semibold text-background" : "text-sm text-background/72"}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (hero.visual === "qualification") {
    return (
      <div className="relative h-full min-h-[520px] overflow-hidden bg-secondary/55 p-7 md:p-9">
        <p className="font-mono text-[10px] uppercase tracking-[.18em] text-primary">{hero.visualLabel}</p>
        <blockquote className="mt-10 max-w-[440px] text-[28px] font-medium leading-[1.28] tracking-[-.025em]">
          « Je voudrais faire estimer mon appartement avant de le mettre en vente. »
        </blockquote>
        <div className="mt-9 border-t border-foreground/20">
          {[
            ["Profil", "Vendeur"],
            ["Intention", "Estimation / vente"],
            ["Secteur", "Toulouse centre"],
            ["Échéance", "Moins de 3 mois"],
          ].map(([k,v])=>(
            <div key={k} className="grid grid-cols-[1fr_auto] border-b border-border py-4 text-sm">
              <span className="text-muted-foreground">{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-primary"><UserRoundCheck className="h-4 w-4"/>Rappel préparé avec le contexte</div>
      </div>
    );
  }

  if (hero.visual === "missed") {
    return (
      <div className="relative h-full min-h-[520px] overflow-hidden bg-secondary/50 p-7 md:p-9">
        <p className="font-mono text-[10px] uppercase tracking-[.18em] text-primary">{hero.visualLabel}</p>
        <div className="mt-10 grid gap-6">
          <div className="border-l-2 border-accent pl-5 opacity-55">
            <div className="flex items-center gap-2 text-sm font-semibold"><Phone className="h-4 w-4"/>14:17 · Appel manqué</div>
            <p className="mt-2 text-sm text-muted-foreground">Aucun motif. Aucun contexte. Un numéro à rappeler.</p>
          </div>
          <ArrowRight className="ml-5 h-5 w-5 rotate-90 text-primary" />
          <div className="border-l-2 border-primary pl-5">
            <div className="flex items-center gap-2 text-sm font-semibold"><PhoneCall className="h-4 w-4 text-primary"/>14:17 · Immovox répond</div>
            <p className="mt-3 text-[24px] font-semibold tracking-[-.03em]">Vendeur · estimation · projet &lt; 3 mois</p>
            <p className="mt-2 text-sm text-muted-foreground">La demande est structurée avant même votre rappel.</p>
          </div>
        </div>
        <div className="absolute bottom-8 left-7 right-7 border-t border-border pt-5 md:left-9 md:right-9">
          <p className="text-[15px] leading-7 text-foreground/72">{hero.visualText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[520px] overflow-hidden">
      <img src="/hero.png" alt="" className="absolute inset-0 h-full w-full object-cover object-[62%_center]" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground/35 via-transparent to-transparent" />
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-background via-background/75 to-transparent" />
      <div className="absolute bottom-7 left-7 right-7 bg-background/[.985] p-5 shadow-warm-lg">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center bg-primary text-primary-foreground"><Route className="h-4 w-4"/></span><div><p className="text-xs text-muted-foreground">{hero.visualLabel}</p><p className="text-sm font-semibold">{hero.visualTitle}</p></div></div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-primary">Qualifié</span>
        </div>
        <p className="mt-3 text-[15px] leading-6">{hero.visualText}</p>
        <div className="mt-4 grid grid-cols-3 gap-3 text-xs"><div><span className="block text-muted-foreground">Profil</span><b>Vendeur</b></div><div><span className="block text-muted-foreground">Motif</span><b>Estimation</b></div><div><span className="block text-muted-foreground">Suite</span><b>Rappel</b></div></div>
      </div>
    </div>
  );
}

export default function IntentLandingLayout(props:IntentLandingLayoutProps){
  const {canonical,title,description,eyebrow,h1,lede,definition,serviceType,primaryCtaLabel,sections,comparison,faq,related,ctaTitle,ctaText,acquisitionHero}=props;
  const [calendlyOpen,setCalendlyOpen]=useState(false);
  const serviceJsonLd={"@context":"https://schema.org","@type":"Service",name:h1,serviceType,description,url:`${BASE_URL}${canonical}`,provider:{"@type":"Organization",name:"Immovox",url:BASE_URL},areaServed:{"@type":"Country",name:"France"},audience:{"@type":"BusinessAudience",audienceType:"Agences immobilières"}};
  const breadcrumbJsonLd={"@context":"https://schema.org","@type":"BreadcrumbList",itemListElement:[{"@type":"ListItem",position:1,name:"Accueil",item:`${BASE_URL}/`},{"@type":"ListItem",position:2,name:eyebrow,item:`${BASE_URL}${canonical}`}]};

  return <div className="min-h-screen bg-background">
    <SEOHead title={title} description={description} canonical={canonical}/>
    <script type="application/ld+json" dangerouslySetInnerHTML={{__html:JSON.stringify(serviceJsonLd)}}/>
    <script type="application/ld+json" dangerouslySetInnerHTML={{__html:JSON.stringify(breadcrumbJsonLd)}}/>
    <Header/>
    <main>
      {acquisitionHero ? (
        <>
          <section className="pt-[72px]">
            <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-12">
              <div className="grid overflow-hidden border-x border-border lg:grid-cols-12">
                <div className="flex min-h-[620px] flex-col justify-center px-5 py-14 md:px-10 lg:col-span-7 lg:px-14">
                  <p className="eyebrow mb-7">{eyebrow}</p>
                  <h1 className="max-w-[800px] text-[46px] font-semibold leading-[.99] tracking-[-.05em] sm:text-[60px] lg:text-[68px]">{h1}</h1>
                  <p className="mt-7 max-w-[640px] text-[17px] leading-7 text-foreground/65 md:text-lg">{lede}</p>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <a href={signupUrl()} className="btn-primary">{primaryCtaLabel}<ArrowRight className="h-4 w-4"/></a>
                    {acquisitionHero.showPhoneDemo ? (
                      <a href="tel:+33517948403" className="btn-secondary"><Phone className="h-4 w-4 text-primary"/>Appeler la démo</a>
                    ) : (
                      <button onClick={()=>setCalendlyOpen(true)} className="btn-secondary"><Calendar className="h-4 w-4 text-primary"/>{acquisitionHero.secondaryCtaLabel || "Voir une démo"}</button>
                    )}
                  </div>
                  <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-muted-foreground">
                    <span className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary"/>7 jours gratuits</span>
                    <span className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary"/>Sans carte bancaire</span>
                    <span className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary"/>Votre numéro reste le même</span>
                  </div>
                </div>
                <div className="border-t border-border lg:col-span-5 lg:border-l lg:border-t-0"><HeroVisual hero={acquisitionHero}/></div>
              </div>
            </div>
          </section>
          <section className="border-y border-border bg-secondary/35">
            <div className="mx-auto grid max-w-[1344px] md:grid-cols-3">
              {acquisitionHero.strip.map((item,i)=>(
                <div key={item} className={"px-5 py-5 md:px-7 "+(i?"border-t border-border md:border-l md:border-t-0":"")}>
                  <span className="font-mono text-[10px] text-primary">0{i+1}</span>
                  <p className="mt-1 text-sm font-medium">{item}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <section className="pt-32 pb-14">
          <div className="mx-auto max-w-[1344px] px-5 md:px-8">
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8"><p className="eyebrow mb-6">{eyebrow}</p><h1 className="max-w-[980px] text-4xl font-semibold tracking-[-.045em] leading-[1.02] md:text-6xl">{h1}</h1></div>
              <div className="lg:col-span-4 lg:pt-14"><p className="text-[17px] leading-7 text-foreground/65">{lede}</p><div className="mt-7 flex flex-wrap gap-3"><a href={signupUrl()} className="btn-primary">{primaryCtaLabel}<ArrowRight className="h-4 w-4"/></a><button onClick={()=>setCalendlyOpen(true)} className="btn-secondary"><Calendar className="h-4 w-4"/>Voir une démo</button></div></div>
            </div>
          </div>
        </section>
      )}

      <section className="border-y border-border bg-secondary/45">
        <div className="mx-auto grid max-w-[1344px] gap-6 px-5 py-8 md:px-8 lg:grid-cols-12">
          <p className="eyebrow lg:col-span-2">En bref</p>
          <div className="text-[17px] leading-8 text-foreground/82 lg:col-span-8">{definition}</div>
        </div>
      </section>

      {sections.map((section,index)=><section key={section.title} className={index%2===0?"py-14 md:py-16 lg:py-20":"py-14 md:py-16 lg:py-20 bg-secondary/35"}>
        <div className="mx-auto grid max-w-[1344px] gap-8 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-4"><span className="font-mono text-[10px] text-primary">0{index+1}</span><h2 className="mt-4 text-3xl font-semibold tracking-[-.035em] md:text-4xl">{section.title}</h2></div>
          <div className="lg:col-span-7 lg:col-start-6">
            {section.intro&&<p className="mb-5 text-[17px] leading-7 text-foreground/68">{section.intro}</p>}
            {section.paragraphs?.map(p=><p key={p} className="mb-4 text-[16px] leading-7 text-foreground/72">{p}</p>)}
            {section.bullets&&<div className="border-t border-foreground/18">{section.bullets.map(b=><div key={b} className="grid grid-cols-[32px_1fr] gap-3 border-b border-border py-4"><CheckCircle2 className="mt-0.5 h-4 w-4 text-primary"/><span className="text-[15px] leading-6 text-foreground/82">{b}</span></div>)}</div>}
          </div>
        </div>
      </section>)}

      {comparison&&<section className="py-16 md:py-20"><div className="mx-auto max-w-[1120px] px-5 md:px-8"><p className="eyebrow mb-4">Comparatif</p><h2 className="text-3xl font-semibold tracking-[-.035em] md:text-4xl">{comparison.title}</h2>{comparison.intro&&<p className="mt-4 max-w-3xl text-[16px] leading-7 text-muted-foreground">{comparison.intro}</p>}<div className="mt-8 overflow-x-auto border-y border-foreground/20"><table className="w-full text-left"><thead><tr>{comparison.headers.map(h=><th key={h} className="px-4 py-4 text-sm font-semibold">{h}</th>)}</tr></thead><tbody>{comparison.rows.map(row=><tr key={row[0]} className="border-t border-border">{row.map((cell,i)=><td key={i} className="px-4 py-4 text-[14px] leading-6 text-foreground/75">{cell}</td>)}</tr>)}</tbody></table></div></div></section>}

      <section className="bg-secondary/45 py-16 md:py-20"><div className="mx-auto max-w-[1120px] px-5 md:px-8"><div className="grid gap-8 lg:grid-cols-12"><div className="lg:col-span-4"><p className="eyebrow">FAQ</p><h2 className="mt-4 text-3xl font-semibold tracking-[-.035em]">Questions fréquentes</h2></div><div className="border-t border-foreground/20 lg:col-span-7 lg:col-start-6">{faq.map(item=><div key={item.question} className="border-b border-border py-5"><h3 className="text-[16px] font-semibold">{item.question}</h3><p className="mt-2 text-[14px] leading-6 text-muted-foreground">{item.answer}</p></div>)}</div></div></div></section>

      <section className="py-14 md:py-16"><div className="mx-auto max-w-[1120px] px-5 md:px-8"><p className="eyebrow mb-5">À lire aussi</p><div className="grid border-t border-foreground/20 md:grid-cols-3">{related.map((item,i)=><Link key={item.to} to={item.to} className={"group py-6 md:px-6 "+(i?"md:border-l md:border-border":"")}><p className="font-semibold group-hover:text-primary">{item.label}</p><p className="mt-2 text-sm leading-6 text-muted-foreground">{item.description}</p></Link>)}</div></div></section>

      <section className="bg-[hsl(157_24%_15%)] text-background"><div className="mx-auto grid max-w-[1344px] gap-8 px-5 py-16 md:px-8 lg:grid-cols-12 lg:items-end"><div className="lg:col-span-7"><p className="font-mono text-[10px] uppercase tracking-[.18em] text-background/55">Tester sur vos appels</p><h2 className="mt-5 text-4xl font-semibold tracking-[-.04em] md:text-5xl">{ctaTitle}</h2></div><div className="lg:col-span-4 lg:col-start-9"><p className="text-[15px] leading-7 text-background/72">{ctaText}</p><a href={signupUrl()} className="mt-6 inline-flex h-12 items-center gap-2 bg-background px-5 text-sm font-semibold text-foreground">Démarrer l'essai gratuit<ArrowRight className="h-4 w-4"/></a></div></div></section>
    </main>
    <Footer/><CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} source={canonical.slice(1)}/>
  </div>;
}
